import { useEffect, useRef, useState } from "react"
import type { ReactNode } from "react"

import type { UseMutationResult, UseQueryResult } from "@tanstack/react-query"
import { clsx } from "clsx"

import {
  SUNGAGE_APPROVED_TERMS_HEADING,
  SUNGAGE_CHECK_UNAVAILABLE,
  SUNGAGE_CREDIT_HEADING,
  SUNGAGE_ESTIMATE_LABEL,
  SUNGAGE_FINANCE_PLAN_MISMATCH_LABEL,
  SUNGAGE_NEXT_STEP_LABEL,
  SUNGAGE_REFRESH_FAILED_PROMPT,
  SUNGAGE_RESUME_BUSY_LABEL,
  SUNGAGE_RESUME_LABEL,
  SUNGAGE_SUBMITTED_TERMS_HEADING,
  SUNGAGE_TERMS_CHANGED_LABEL,
  SUNGAGE_VERIFIED_APPROVED_LABEL,
  sungageApplicationOutcome,
} from "./sungage-application-copy"
import {
  SungagePaymentDetailsTable,
  type SungagePaymentDetailsView,
} from "./sungage-payment-details"
import { SungageHostedApplication } from "./SungageHostedApplication"
import { APPLICATION_ACTIVITY_HEADING } from "../../../BatteryOnlyProposal/financing-status/application-status-labels"
import {
  CONCERT_SUPPORT_GUIDANCE,
  OUTCOME_TONE_CLASS,
} from "../concert/concert-application-copy"

/** The Sungage application read every kind's gateway returns (the shared core's shape). */
export type SungageApplicationView = {
  applicationId: string
  providerReference: string | null
  creditStatus: string
  projectProgress: {
    nextStep: string | null
    dateFunded: string | null
    webhookEventName: string | null
  }
  submittedAt: string | null
  expiration: { kind: string; creditDeadline?: string }
  stipulations: string[]
  nextStep: string | null
  submittedTerms: {
    termYears: number
    rate: number
    paymentDetails: SungagePaymentDetailsView
  }
  approvedTerms: {
    termYears: number
    rate: number
    paymentDetails: SungagePaymentDetailsView
  } | null
  termsKind: `estimate` | `verified-approved`
  reviewNeeded: boolean
  reviewReasons: string[]
  financePlanMismatch: boolean
  refreshState: `failed` | `idle`
  activity: Array<{ at: string; kind: string; summary: string }>
  capabilities: {
    resume: { allowed: boolean; reason?: string | null }
    refresh: { allowed: boolean; reason?: string | null }
  }
}

type SungageApplicationTarget = { proposalId: string; applicationId: string }

/**
 * What a Proposal Kind plugs into the shared Sungage status: its reads and
 * commands bound to its endpoints, its program name and its test-id prefix.
 * Bundles are module constants, so the hooks inside run in a stable order.
 */
export type SungageApplicationStatusBundle<A extends SungageApplicationView> = {
  /** `${prefix}-sungage-…` test ids. */
  readonly testIdPrefix: string
  /** The program in the outcome copy: BrightStart, BrightStar, Sunrise Escalator. */
  readonly programLabel: (application: A) => string
  /** Above the hosted application's title. */
  readonly hostedEyebrow: string
  readonly useApplication: (
    proposalId: string,
    applicationId: string,
  ) => UseQueryResult<A | null>
  readonly useRefresh: () => UseMutationResult<
    A | null,
    unknown,
    SungageApplicationTarget
  >
  readonly useResume: () => UseMutationResult<
    { applicationUrl: string | null },
    unknown,
    SungageApplicationTarget
  >
  /** The seller-facing reason a command failed. */
  readonly describeError: (error: unknown) => string
  /** Rows the kind adds under a payment (Solar: amount due, escalation). */
  readonly paymentExtras?: (
    application: A,
    terms: `approved` | `submitted`,
  ) => Array<{ label: string; value: string }>
}

const sectionClass = `rounded-lg border border-bp-border bg-bp-elevated`
const primaryButtonClass = `rounded-md bg-bp-accent px-4 py-2 text-[length:var(--step--1)] font-semibold text-bp-on-accent hover:bg-bp-accent-hover disabled:cursor-not-allowed disabled:opacity-50`
const REFRESH_WHILE_OPEN_MS = 45_000

const dateTimeLabel = (iso: string): string =>
  new Intl.DateTimeFormat(`en-US`, {
    year: `numeric`,
    month: `short`,
    day: `numeric`,
    hour: `numeric`,
    minute: `2-digit`,
  }).format(new Date(iso))

const expirationLabel = (
  expiration: SungageApplicationView[`expiration`],
): string => {
  switch (expiration.kind) {
    case `expired`:
      return expiration.creditDeadline
        ? `Expired ${expiration.creditDeadline}`
        : `Expired`
    case `valid`:
      return expiration.creditDeadline
        ? `Expires ${expiration.creditDeadline}`
        : `Valid`
    default:
      return SUNGAGE_CHECK_UNAVAILABLE
  }
}

const termsChanged = (
  submitted: SungageApplicationView[`submittedTerms`],
  approved: NonNullable<SungageApplicationView[`approvedTerms`]>,
): boolean => {
  const original = submitted.paymentDetails
  const next = approved.paymentDetails
  return (
    submitted.termYears !== approved.termYears ||
    submitted.rate !== approved.rate ||
    original.apr !== next.apr ||
    original.initialMonthlyPayment !== next.initialMonthlyPayment ||
    original.continuingMonthlyPayment !== next.continuingMonthlyPayment ||
    original.maxMonthlyPayment !== next.maxMonthlyPayment ||
    original.deferredPaymentPortion !== next.deferredPaymentPortion ||
    original.dppProduct !== next.dppProduct ||
    original.deferredPeriodMonths !== next.deferredPeriodMonths ||
    original.assumedPaydownAmount !== next.assumedPaydownAmount ||
    original.achAssumed !== next.achAssumed
  )
}

function Section({
  title,
  testId,
  trailing,
  children,
}: {
  title: string
  testId: string
  trailing?: ReactNode
  children: ReactNode
}): JSX.Element {
  return (
    <section aria-label={title} data-testid={testId} className={sectionClass}>
      <div className="flex items-center justify-between gap-3 border-b border-bp-border px-4 py-3">
        <h3 className="text-[length:var(--step--1)] font-semibold text-bp-text">
          {title}
        </h3>
        {trailing}
      </div>
      {children}
    </section>
  )
}

/**
 * The first card: what Sungage says now and the one thing to do next.
 */
function Summary({
  application,
  programLabel,
  testIdPrefix,
  resumeBusy,
  resumeError,
  onResume,
}: {
  application: SungageApplicationView
  programLabel: string
  testIdPrefix: string
  resumeBusy: boolean
  resumeError: ReactNode
  onResume: () => void
}): JSX.Element {
  const outcome = sungageApplicationOutcome(application, programLabel)
  return (
    <section
      aria-label="Application"
      data-testid={`${testIdPrefix}-sungage-application-summary`}
      className={sectionClass}
    >
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-bp-border px-4 py-3">
        <span
          data-testid={`${testIdPrefix}-sungage-status-badge`}
          className={clsx(
            `inline-flex items-center rounded-full border px-2.5 py-0.5 text-[length:var(--step--2)] font-semibold`,
            OUTCOME_TONE_CLASS[outcome.tone],
          )}
        >
          {outcome.badge}
        </span>
        <span
          data-testid={`${testIdPrefix}-sungage-reference`}
          className="text-[length:var(--step--2)] text-bp-text-muted tabular-nums"
        >
          {application.providerReference != null
            ? `Sungage reference ${application.providerReference}`
            : `Sungage reference ${SUNGAGE_CHECK_UNAVAILABLE}`}
        </span>
      </div>
      <div
        data-testid={`${testIdPrefix}-sungage-outcome`}
        className="flex flex-col gap-3 px-4 py-4"
      >
        <div>
          <p
            data-testid={`${testIdPrefix}-sungage-outcome-title`}
            className="text-[length:var(--step-0)] font-semibold text-bp-text"
          >
            {outcome.title}
          </p>
          <p className="mt-1 text-[length:var(--step--1)] text-bp-text-muted">
            {outcome.detail}
          </p>
          {outcome.supportNeeded ? (
            <p
              data-testid={`${testIdPrefix}-sungage-support-guidance`}
              className="mt-2 text-[length:var(--step--1)] text-bp-gold"
            >
              {CONCERT_SUPPORT_GUIDANCE}
            </p>
          ) : null}
        </div>
        {application.nextStep != null && !outcome.continueApplication ? (
          <p
            data-testid={`${testIdPrefix}-sungage-next-step`}
            className="text-[length:var(--step--1)] text-bp-text"
          >
            <span className="text-bp-text-muted">
              {SUNGAGE_NEXT_STEP_LABEL}:
            </span>
            {` `}
            <span className="font-semibold">{application.nextStep}</span>
          </p>
        ) : null}
        {application.stipulations.length > 0 ? (
          <ul
            data-testid={`${testIdPrefix}-sungage-stipulations`}
            aria-label="Stipulations"
            className="flex flex-wrap gap-2"
          >
            {application.stipulations.map((stipulation) => (
              <li
                key={stipulation}
                className="rounded-full border border-bp-border px-2.5 py-0.5 text-[length:var(--step--2)] text-bp-text"
              >
                {stipulation}
              </li>
            ))}
          </ul>
        ) : null}
        {outcome.continueApplication &&
        application.capabilities.resume.allowed ? (
          <button
            type="button"
            data-testid={`${testIdPrefix}-sungage-resume`}
            className={clsx(primaryButtonClass, `self-start`)}
            disabled={resumeBusy}
            onClick={onResume}
          >
            {resumeBusy ? SUNGAGE_RESUME_BUSY_LABEL : SUNGAGE_RESUME_LABEL}
          </button>
        ) : null}
        {resumeError}
      </div>
    </section>
  )
}

function PaymentBlock({
  details,
  extras,
  testId,
}: {
  details: SungagePaymentDetailsView
  extras: Array<{ label: string; value: string }>
  testId: string
}): JSX.Element {
  return (
    <div data-testid={testId} className="px-0">
      <SungagePaymentDetailsTable details={details} extras={extras} />
    </div>
  )
}

/**
 * The Sungage application's status for any Proposal Kind: what Sungage says
 * now and the next step, credit and expiration, the submitted and approved
 * terms ("terms changed" only when they differ), activity, and the hosted
 * application on Resume. Opening refreshes once, then every 45 seconds while
 * open; every figure is the server's.
 */
export function SungageApplicationStatus<A extends SungageApplicationView>({
  bundle,
  proposalId,
  applicationId,
  initial,
}: {
  bundle: SungageApplicationStatusBundle<A>
  proposalId: string
  applicationId: string
  initial: A | null
}): JSX.Element {
  const { testIdPrefix: prefix } = bundle
  const saved = bundle.useApplication(proposalId, applicationId)
  const refresh = bundle.useRefresh()
  const resume = bundle.useResume()
  const [hostedUrl, setHostedUrl] = useState<string | null>(null)
  const opened = useRef(false)
  const application = saved.data ?? refresh.data ?? initial

  const refreshMutate = refresh.mutate
  const refreshing = useRef(false)

  useEffect(() => {
    if (opened.current || applicationId.length === 0 || saved.isLoading) {
      return
    }
    opened.current = true
    if (saved.data?.capabilities.refresh.allowed === true) {
      refreshing.current = true
      refreshMutate(
        { proposalId, applicationId },
        {
          onSettled: () => {
            refreshing.current = false
          },
        },
      )
    }
  }, [
    applicationId,
    proposalId,
    refreshMutate,
    saved.data?.capabilities.refresh.allowed,
    saved.isLoading,
  ])

  useEffect(() => {
    if (application?.capabilities.refresh.allowed !== true) {
      return undefined
    }
    const timer = window.setInterval(() => {
      if (refreshing.current) {
        return
      }
      refreshing.current = true
      refreshMutate(
        { proposalId, applicationId },
        {
          onSettled: () => {
            refreshing.current = false
          },
        },
      )
    }, REFRESH_WHILE_OPEN_MS)
    return () => window.clearInterval(timer)
  }, [
    application?.capabilities.refresh.allowed,
    applicationId,
    proposalId,
    refreshMutate,
  ])

  if (application == null) {
    return (
      <div
        role={saved.isError ? `alert` : `status`}
        data-testid={`${prefix}-sungage-application-loading`}
        className="text-[length:var(--step--1)] text-bp-text-muted"
      >
        {saved.isError
          ? `Sungage application status could not be loaded.`
          : `Loading Sungage application…`}
      </div>
    )
  }

  const approved = application.approvedTerms
  const submittedDetails = application.submittedTerms.paymentDetails
  const changed =
    approved != null && termsChanged(application.submittedTerms, approved)
  const refreshFailed = refresh.isError || application.refreshState === `failed`

  return (
    <div
      data-testid={`${prefix}-sungage-financing-status`}
      className="flex flex-col gap-4"
    >
      <Summary
        application={application}
        programLabel={bundle.programLabel(application)}
        testIdPrefix={prefix}
        resumeBusy={resume.isLoading}
        resumeError={
          resume.isError ? (
            <p
              role="alert"
              className="text-[length:var(--step--2)] text-bp-danger"
            >
              {bundle.describeError(resume.error)}
            </p>
          ) : null
        }
        onResume={() => {
          resume.mutate(
            { proposalId, applicationId },
            {
              onSuccess: (handoff) => {
                if (handoff.applicationUrl != null) {
                  setHostedUrl(handoff.applicationUrl)
                }
              },
            },
          )
        }}
      />
      {application.financePlanMismatch ? (
        <p
          role="status"
          data-testid={`${prefix}-sungage-finance-plan-mismatch`}
          className="rounded-md border border-bp-gold/40 bg-bp-gold/10 px-3 py-2 text-[length:var(--step--2)] text-bp-gold"
        >
          {SUNGAGE_FINANCE_PLAN_MISMATCH_LABEL}
        </p>
      ) : null}
      {refreshFailed ? (
        <p
          role="alert"
          data-testid={`${prefix}-sungage-refresh-error`}
          className="text-[length:var(--step--2)] text-bp-gold"
        >
          {refresh.isError
            ? bundle.describeError(refresh.error)
            : SUNGAGE_REFRESH_FAILED_PROMPT}
        </p>
      ) : null}

      <Section
        title={SUNGAGE_CREDIT_HEADING}
        testId={`${prefix}-sungage-credit`}
      >
        <dl className="grid grid-cols-[minmax(8rem,auto)_1fr] gap-x-4 gap-y-2 px-4 py-4 text-[length:var(--step--2)] text-bp-text">
          <dt className="text-bp-text-muted">Submitted</dt>
          <dd>
            {application.submittedAt != null
              ? dateTimeLabel(application.submittedAt)
              : SUNGAGE_CHECK_UNAVAILABLE}
          </dd>
          <dt className="text-bp-text-muted">Approval expiration</dt>
          <dd data-testid={`${prefix}-sungage-expiration`}>
            {expirationLabel(application.expiration)}
          </dd>
          <dt className="text-bp-text-muted">Terms</dt>
          <dd>
            {application.termsKind === `verified-approved`
              ? SUNGAGE_VERIFIED_APPROVED_LABEL
              : SUNGAGE_ESTIMATE_LABEL}
          </dd>
        </dl>
      </Section>

      <Section
        title={SUNGAGE_SUBMITTED_TERMS_HEADING}
        testId={`${prefix}-sungage-submitted-terms`}
      >
        <PaymentBlock
          details={submittedDetails}
          extras={bundle.paymentExtras?.(application, `submitted`) ?? []}
          testId={`${prefix}-sungage-submitted-payment`}
        />
      </Section>

      {approved != null ? (
        <Section
          title={SUNGAGE_APPROVED_TERMS_HEADING}
          testId={`${prefix}-sungage-approved-terms`}
        >
          {changed ? (
            <p
              data-testid={`${prefix}-sungage-terms-changed`}
              className="px-4 pt-3 text-[length:var(--step--2)] text-bp-gold"
            >
              {SUNGAGE_TERMS_CHANGED_LABEL}
            </p>
          ) : null}
          <PaymentBlock
            details={approved.paymentDetails}
            extras={bundle.paymentExtras?.(application, `approved`) ?? []}
            testId={`${prefix}-sungage-approved-payment`}
          />
        </Section>
      ) : null}

      <Section
        title={APPLICATION_ACTIVITY_HEADING}
        testId={`${prefix}-sungage-activity`}
      >
        {application.activity.length === 0 ? (
          <p className="px-4 py-4 text-[length:var(--step--2)] text-bp-text-muted">
            No activity yet.
          </p>
        ) : (
          <ol className="divide-y divide-bp-border">
            {application.activity.map((event) => (
              <li
                key={`${event.at}-${event.kind}-${event.summary}`}
                className="px-4 py-3 text-[length:var(--step--2)]"
              >
                <p className="font-medium text-bp-text">{event.summary}</p>
                <p className="text-bp-text-muted">{dateTimeLabel(event.at)}</p>
              </li>
            ))}
          </ol>
        )}
      </Section>

      {hostedUrl != null ? (
        <SungageHostedApplication
          url={hostedUrl}
          onClose={() => setHostedUrl(null)}
          eyebrow={bundle.hostedEyebrow}
          testIdPrefix={prefix}
        />
      ) : null}
    </div>
  )
}
